import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Button } from '@mui/material';
import { Inventory2, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { RouterLink } from '../../utils/linkNav';

const typeLabel: Record<string, string> = { RAW: 'Raw Material', SEMI: 'Work in Progress', FINISHED: 'Finished Goods', CONSUMABLE: 'Consumables', OTHER: 'Other' };

const InventoryControlTower: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const typeColor: Record<string, string> = { RAW: C.cyan, SEMI: C.warn, FINISHED: C.good, CONSUMABLE: C.sub, OTHER: C.sub };
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { financeWorkspaceService.inventoryControl().then(setD).catch(() => setD(null)).finally(() => setLoading(false)); }, []);
  const h = d?.health || {};
  const healthCards = [
    { k: 'Healthy', v: h.healthy, c: C.good }, { k: 'Low Stock', v: h.low, c: C.warn },
    { k: 'Critical', v: h.critical, c: C.bad }, { k: 'Excess', v: h.excess, c: C.accent },
  ];

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: `linear-gradient(90deg, ${C.panel}, ${C.bg})` }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Inventory2 sx={{ color: C.cyan }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Inventory Control Tower</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>Total value {d ? formatIndianNumber(d.totalValue) : '—'} · stock health & movement</Typography>
          </Box>
        </Stack>
        <Button variant="outlined" onClick={() => navigate('/inventory/stock')} sx={{ color: C.text, borderColor: C.line }}>Stock List</Button>
      </Box>
      {loading && <LinearProgress />}

      {/* Value by material type */}
      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>Value by Material Type</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 1.5 }}>
          {(d?.byType || []).map((t: any) => (
            <Box key={t.type} sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderLeft: `3px solid ${typeColor[t.type] || C.sub}`, borderRadius: 2, p: 2 }}>
              <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase' }}>{typeLabel[t.type] || t.type}</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{formatIndianNumber(t.value)}</Typography>
              <Typography sx={{ color: C.sub, fontSize: 11 }}>{t.items} items</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* Stock health */}
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>Stock Health · {h.total || 0} SKUs</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1.5, mb: 2 }}>
            {healthCards.map((x) => (
              <Box key={x.k} sx={{ bgcolor: C.panel2, border: `1px solid ${C.line}`, borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 800, fontSize: 22, color: x.c, fontFamily: 'monospace' }}>{x.v ?? 0}</Typography>
                <Typography sx={{ fontSize: 11, color: C.sub }}>{x.k}</Typography>
              </Box>
            ))}
          </Box>
          {/* proportion bar */}
          <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden' }}>
            {healthCards.map((x) => h.total ? <Box key={x.k} sx={{ width: `${((x.v || 0) / h.total) * 100}%`, bgcolor: x.c }} /> : null)}
          </Box>
        </Box>

        {/* Today's movement */}
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.5 }}>Today's Movement</Typography>
          <Stack spacing={1}>
            {(d?.movements || []).map((mv: any) => (
              <Box key={mv.type} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: C.panel2, borderRadius: 1 }}>
                {mv.type === 'IN' ? <ArrowDownward sx={{ color: C.good }} /> : <ArrowUpward sx={{ color: C.warn }} />}
                <Typography sx={{ flex: 1, fontSize: 13 }}>{mv.type === 'IN' ? 'Received' : mv.type === 'OUT' ? 'Issued / Dispatched' : mv.type}</Typography>
                <Typography sx={{ fontFamily: 'monospace', color: mv.type === 'IN' ? C.good : C.warn }}>{mv.type === 'IN' ? '+' : '-'}{mv.qty}</Typography>
                <Typography sx={{ fontSize: 11, color: C.sub }}>{mv.count} moves</Typography>
              </Box>
            ))}
            {!loading && !(d?.movements || []).length && <Typography sx={{ color: C.sub, fontSize: 13, py: 1 }}>No stock movement today.</Typography>}
          </Stack>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 2, mb: 1 }}>Top Items by Value</Typography>
          <Stack spacing={0.5}>
            {(d?.topItems || []).slice(0, 6).map((it: any, i: number) => (
              <Stack key={i} direction="row" justifyContent="space-between" {...(it.productId ? { component: RouterLink, to: `/entity/material/${it.productId}` } : {})} sx={{ cursor: it.productId ? 'pointer' : 'default', textDecoration: 'none', color: 'inherit', '&:hover .pn': { color: C.accent } }}>
                <Typography className="pn" sx={{ fontSize: 12, color: C.text }} noWrap>{it.product}</Typography>
                <Typography sx={{ fontSize: 12, color: C.sub, fontFamily: 'monospace' }}>{formatIndianNumber(it.value)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default InventoryControlTower;
